import { useState } from "react";
import { 
  ArrowRight, 
  Mail,
  Phone,
  Instagram,
  MapPin,
  Send,
  MessageCircle,
  CheckCircle
} from "lucide-react";

const ContatoPage = () => {
  const [formData, setFormData] = useState({
    nome: "",
    empresa: "",
    email: "",
    telefone: "",
    interesse: "",
    mensagem: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const interesses = [
    "Recrutamento e Seleção",
    "Avaliação Psicológica",
    "Desenvolvimento de Liderança",
    "Treinamentos Corporativos",
    "Consultoria em Gestão de Pessoas",
    "Mentoria para Empreendedores",
    "Clima Organizacional",
    "Outro"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Build WhatsApp message
    const message = `Olá! Meu nome é ${formData.nome}.

*Empresa:* ${formData.empresa || 'Não informada'}
*E-mail:* ${formData.email}
*Telefone:* ${formData.telefone || 'Não informado'}
*Interesse:* ${formData.interesse || 'Não especificado'}

*Mensagem:*
${formData.mensagem || 'Gostaria de saber mais sobre os serviços.'}`;

    const whatsappUrl = `https://wa.me/5586998325746?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: "E-mail",
      value: "contato@lylianlopesconsultoria.com.br",
      href: "mailto:contato@lylianlopesconsultoria.com.br"
    },
    {
      icon: Phone,
      label: "WhatsApp",
      value: "(86) 99832-5746",
      href: "https://wa.me/5586998325746"
    },
    {
      icon: Instagram,
      label: "Instagram",
      value: "@lylopespsi",
      href: "https://instagram.com/lylopespsi"
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contato-page">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-brand-base-clara via-[#E5DBC5] to-brand-surface relative overflow-hidden" data-testid="contato-hero">
        {/* Decorative lines */}
        <div className="absolute right-12 lg:right-24 top-1/4 bottom-1/4 flex gap-3">
          <div className="w-[2px] h-full bg-brand-terra-humana/20" />
          <div className="w-[2px] h-full bg-brand-terracota/30 mt-8" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-muted mb-6 flex items-center gap-3">
              <span className="w-6 h-[1px] bg-brand-muted" />
              CONTATO
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-brand-neutro leading-[1.1] mb-6">
              Vamos conversar sobre{" "}
              <span className="italic text-brand-terracota">sua empresa?</span>
            </h1>
            <p className="font-sans text-lg lg:text-xl text-brand-muted leading-relaxed">
              Entre em contato para agendar um diagnóstico estratégico gratuito ou tirar dúvidas sobre nossos serviços.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="py-16 lg:py-24 bg-brand-surface" data-testid="contato-form-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-brand-base-clara p-8 lg:p-12 rounded-2xl">
                <h2 className="font-serif text-2xl font-medium text-brand-neutro mb-2">
                  Envie sua mensagem
                </h2>
                <p className="font-sans text-brand-muted mb-8">
                  Preencha o formulário abaixo e entraremos em contato pelo WhatsApp.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="nome" className="block font-sans text-sm font-medium text-brand-neutro mb-2">
                        Nome completo *
                      </label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        required
                        value={formData.nome}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/50 border border-brand-border rounded-xl font-sans text-brand-neutro placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-terracota/30 focus:border-brand-terracota transition-premium"
                        placeholder="Seu nome"
                        data-testid="input-nome"
                      />
                    </div>
                    <div>
                      <label htmlFor="empresa" className="block font-sans text-sm font-medium text-brand-neutro mb-2">
                        Empresa
                      </label>
                      <input
                        type="text"
                        id="empresa"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/50 border border-brand-border rounded-xl font-sans text-brand-neutro placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-terracota/30 focus:border-brand-terracota transition-premium"
                        placeholder="Nome da empresa"
                        data-testid="input-empresa"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block font-sans text-sm font-medium text-brand-neutro mb-2">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/50 border border-brand-border rounded-xl font-sans text-brand-neutro placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-terracota/30 focus:border-brand-terracota transition-premium"
                        placeholder="seu@email.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label htmlFor="telefone" className="block font-sans text-sm font-medium text-brand-neutro mb-2">
                        Telefone / WhatsApp
                      </label>
                      <input
                        type="tel"
                        id="telefone"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/50 border border-brand-border rounded-xl font-sans text-brand-neutro placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-terracota/30 focus:border-brand-terracota transition-premium"
                        placeholder="(00) 00000-0000"
                        data-testid="input-telefone"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="interesse" className="block font-sans text-sm font-medium text-brand-neutro mb-2">
                      Área de interesse
                    </label>
                    <select
                      id="interesse"
                      name="interesse"
                      value={formData.interesse}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/50 border border-brand-border rounded-xl font-sans text-brand-neutro focus:outline-none focus:ring-2 focus:ring-brand-terracota/30 focus:border-brand-terracota transition-premium"
                      data-testid="select-interesse"
                    >
                      <option value="">Selecione uma área</option>
                      {interesses.map((interesse) => (
                        <option key={interesse} value={interesse}>
                          {interesse}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="mensagem" className="block font-sans text-sm font-medium text-brand-neutro mb-2">
                      Mensagem
                    </label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      rows={4}
                      value={formData.mensagem}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/50 border border-brand-border rounded-xl font-sans text-brand-neutro placeholder:text-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-terracota/30 focus:border-brand-terracota transition-premium resize-none"
                      placeholder="Conte um pouco sobre sua empresa e suas necessidades..."
                      data-testid="textarea-mensagem"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-sans font-medium disabled:opacity-50"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      "Enviando..."
                    ) : (
                      <>
                        Enviar pelo WhatsApp
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <div className="sticky top-32">
                <h2 className="font-serif text-2xl font-medium text-brand-neutro mb-6">
                  Outros canais
                </h2>

                <div className="space-y-4 mb-10">
                  {contactInfo.map((info, index) => (
                    <a
                      key={index}
                      href={info.href}
                      target={info.href.startsWith('http') ? '_blank' : undefined}
                      rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="flex items-center gap-4 bg-brand-base-clara p-4 rounded-xl hover:bg-brand-terra-humana/10 transition-premium"
                      data-testid={`contact-info-${index}`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-brand-terra-humana/10 flex items-center justify-center flex-shrink-0">
                        <info.icon className="text-brand-terracota" size={20} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-sans text-xs uppercase tracking-wider text-brand-muted mb-0.5">
                          {info.label}
                        </p>
                        <p className="font-sans text-brand-neutro font-medium">
                          {info.value}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>

                {/* Quick WhatsApp */}
                <div className="bg-brand-verde p-6 lg:p-8 rounded-2xl">
                  <MessageCircle className="text-brand-base-clara/50 mb-4" size={32} strokeWidth={1.5} />
                  <h3 className="font-serif text-xl font-medium text-brand-base-clara mb-2">
                    Prefere conversar diretamente?
                  </h3>
                  <p className="font-sans text-brand-base-clara/80 text-sm mb-6">
                    Clique abaixo para iniciar uma conversa no WhatsApp agora mesmo.
                  </p>
                  <a
                    href="https://wa.me/5586998325746?text=Olá! Gostaria de agendar um diagnóstico estratégico para minha empresa."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-brand-base-clara text-brand-neutro hover:bg-white w-full py-3 rounded-full font-sans font-medium transition-premium"
                    data-testid="quick-whatsapp-button"
                  >
                    Abrir WhatsApp
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Institutional Block */}
      <section className="py-16 lg:py-24 bg-brand-base-clara" data-testid="contato-institucional">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl font-medium text-brand-neutro mb-6">
            Transformação começa com uma conversa.
          </h2>
          <p className="font-sans text-lg text-brand-muted leading-relaxed mb-8 max-w-2xl mx-auto">
            Agende seu diagnóstico estratégico gratuito e descubra como alinhar pessoas, cultura e resultados na sua empresa. Sem compromisso, sem pressão — apenas uma conversa sobre suas reais necessidades.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-brand-verde">
            <div className="flex items-center gap-2">
              <CheckCircle size={18} />
              <span className="font-sans text-sm">Diagnóstico gratuito</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} />
              <span className="font-sans text-sm">Sem compromisso</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} />
              <span className="font-sans text-sm">Atendimento personalizado</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContatoPage;
